// A lot's tax treatment (BL-1767): the three Tax Status values and their labels.
// BL-1904: a taxable status needs no rate at save; `isTaxableLotWithoutRate` flags
// the lots still missing one, since they bill $0.00 sales tax at the sale.
import { z } from '@auctogon/core/zod';

/**
 * The three values `auction_lots.tax_status` may hold. Mirrors the column's
 * CHECK constraint. A data field, not a lifecycle status (rule #23 does not
 * apply). `tax_exempt` is the default on every new lot.
 */
export const LOT_TAX_STATUSES = ['tax_exempt', 'regular', 'new_farm_machinery'] as const;

/** One of {@link LOT_TAX_STATUSES}. */
export type LotTaxStatus = (typeof LOT_TAX_STATUSES)[number];

/** The default every new lot carries. */
export const DEFAULT_LOT_TAX_STATUS: LotTaxStatus = 'tax_exempt';

/** Zod gate for a Tax Status on the wire. */
export const LotTaxStatusSchema = z.enum(LOT_TAX_STATUSES);

/** Dropdown / filter option labels. */
export const LOT_TAX_STATUS_LABELS: Record<LotTaxStatus, string> = {
  tax_exempt: 'Tax exempt',
  regular: 'Regular taxable item',
  new_farm_machinery: 'New farm machinery',
};

/** Shorter labels for the list pill, where column width is tight. */
export const LOT_TAX_STATUS_SHORT_LABELS: Record<LotTaxStatus, string> = {
  tax_exempt: 'Tax exempt',
  regular: 'Regular taxable',
  new_farm_machinery: 'New farm machinery',
};

/**
 * BL-2361: the Tax Status cell words the lot-list CSV export writes. Frozen at
 * their pre-sentence-case bytes so a spreadsheet or import keyed on them keeps
 * working; the screen reads {@link LOT_TAX_STATUS_SHORT_LABELS}. The export
 * reads only this map.
 */
export const LOT_TAX_STATUS_EXPORT_LABELS: Record<LotTaxStatus, string> = {
  tax_exempt: 'Tax Exempt',
  regular: 'Regular Taxable',
  new_farm_machinery: 'New Farm Machinery',
};

/**
 * The Tax Rate filter's wire value for "taxable, no rate" (BL-1904 AC 7). It rides
 * the `taxRateId` URL list beside real `tax_rates.id`s; the inventory filter reads it.
 */
export const TAXABLE_NO_RATE_FILTER_VALUE = 'taxable-no-rate';

/** The option label for {@link TAXABLE_NO_RATE_FILTER_VALUE}. */
export const TAXABLE_NO_RATE_FILTER_LABEL = 'Taxable, no rate';

/**
 * Narrow an unknown value to a {@link LotTaxStatus}.
 *
 * @param value - anything, typically a request-body field.
 * @returns true when it is one of the three statuses.
 */
export function isLotTaxStatus(value: unknown): value is LotTaxStatus {
  return typeof value === 'string' && (LOT_TAX_STATUSES as readonly string[]).includes(value);
}

/**
 * Whether a status puts the lot in the taxable base — every status but Tax Exempt.
 *
 * @param status - the lot's Tax Status.
 * @returns true for Regular Taxable Item and New Farm Machinery.
 */
export function isTaxableLotStatus(status: LotTaxStatus): boolean {
  return status !== 'tax_exempt';
}

/**
 * The statuses that put a lot in the taxable base. Shared by
 * {@link isTaxableLotWithoutRate} and the inventory filter's SQL arm for
 * {@link TAXABLE_NO_RATE_FILTER_VALUE}, so the two cannot disagree.
 */
export const TAXABLE_LOT_STATUSES: readonly LotTaxStatus[] = LOT_TAX_STATUSES.filter(isTaxableLotStatus);

/**
 * Whether a lot is taxable but has no Tax Rate picked yet (BL-1904). The rate is
 * optional at save, and such a lot bills $0.00 sales tax at the sale, so the lot
 * panel and the lot lists flag it. Pure, typed on the two fields it reads (ADR 010
 * — Isomorphic Services), so a form's values and a list row both satisfy it.
 *
 * @param lot - the lot's Tax Status and Tax Rate id, in any shape that carries them.
 * @returns true for a taxable status with no rate; false for Tax Exempt, a picked
 *   rate, or an unrecognised status.
 */
export function isTaxableLotWithoutRate(lot: { taxStatus?: unknown; taxRateId?: unknown }): boolean {
  if (!isLotTaxStatus(lot.taxStatus) || !isTaxableLotStatus(lot.taxStatus)) return false;
  return !(typeof lot.taxRateId === 'string' && lot.taxRateId.length > 0);
}

/**
 * Narrow a stored `tax_status` to the union. The column's CHECK constraint
 * guarantees one of the three, so the fallback is a type-level safety net, not
 * a real branch.
 *
 * @param value - the column value as the driver returns it.
 * @returns the status, or the default when (impossibly) unrecognised.
 */
export function toLotTaxStatus(value: unknown): LotTaxStatus {
  return isLotTaxStatus(value) ? value : DEFAULT_LOT_TAX_STATUS;
}
